import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { FacetRow } from '../components/FacetRow';
import { TileSkeleton } from '../components/ProductTile';
import { withInspiration, INSPIRATION } from '../components/InspirationBlock';
import Seo from '../components/Seo';
import { useLang } from '../i18n';
import { useCategory, useFacets, useProducts } from '../lib/useCatalog';

const PER_PAGE = 24;

/**
 * Pagina categoria: elenco prodotti di una categoria con filtri a chip
 * (shade/materiale/colore, come in Collections.jsx) e paginazione, tutto
 * riflesso in query string. Il filtro categoria non compare: è implicito
 * nello slug della route.
 */
export default function CategoryDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [params, setParams] = useSearchParams();

  const cat = useCategory(slug, lang);
  const facetsQuery = useFacets(slug, lang);

  // ?page=0 / -3 / abc arriverebbero al backend (Query(ge=1)) come 422
  const page = Math.max(1, Math.trunc(Number(params.get('page'))) || 1);
  const filters = {
    shade: params.get('shade') || '',
    material: params.get('material') || '',
    color: params.get('color') || '',
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || next.get(key) === value) next.delete(key);
    else next.set(key, value);
    next.delete('page');
    setParams(next);
  };

  const goToPage = (n) => {
    const next = new URLSearchParams(params);
    next.set('page', String(n));
    setParams(next);
  };

  const products = useProducts({
    ...filters,
    lang,
    page,
    per_page: PER_PAGE,
    category_slug: slug,
  });

  const items = products.data?.items ?? [];
  const total = products.data?.total ?? 0;
  const facets = facetsQuery.data;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  // Solo un 404 vero significa "categoria inesistente": su 5xx o rete giu'
  // la query ritenta (useCategory) e la pagina resta quella normale.
  if (cat.error?.response?.status === 404) {
    return (
      <div className="wrap py-24 text-center">
        <Seo title={t('notFound.seoTitle')} noIndex />
        <h1 className="display-xl">404</h1>
        <p className="prosa mt-4">{t('catalog.notFound')}</p>
        <Link className="btn btn-line mt-8 inline-flex" to="/catalogo">
          {t('catalog.backToCatalog')}
        </Link>
      </div>
    );
  }

  const label = cat.data?.label ?? '';

  return (
    <>
      <Seo
        title={cat.data?.copy?.seo_title || label || t('catalog.seoTitle')}
        suffix={cat.data?.copy?.seo_title ? '' : undefined}
        description={cat.data?.copy?.seo_meta || t('catalog.seoDesc')}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
            { '@type': 'ListItem', position: 2, name: t('catalog.title'), item: '/catalogo' },
            { '@type': 'ListItem', position: 3, name: label, item: `/catalogo/${slug}` },
          ],
        }}
      />

      <nav aria-label="breadcrumb" className="wrap pt-10">
        <Link className="label hover:text-marsala-dark" to="/catalogo">
          {t('catalog.title')}
        </Link>
        {cat.data?.macro && (
          <span className="label text-inchiostro/40"> · {cat.data.macro.label}</span>
        )}
      </nav>

      <header className="wrap pt-4 pb-8">
        <h1 className="display-xl">{label || ' '}</h1>
        <p className="prosa mt-4 max-w-xl">
          {cat.data?.copy?.lead || t('catalog.worldLead')}
        </p>
      </header>

      {facets && (
        <div className="wrap pb-8">
          <div className="space-y-5 py-7">
            <FacetRow
              label={t('common.shade')}
              items={facets.shades}
              active={filters.shade}
              onPick={(v) => setFilter('shade', v)}
              testid="facet-shade"
            />
            <FacetRow
              label={t('common.materials')}
              items={facets.materials}
              active={filters.material}
              onPick={(v) => setFilter('material', v)}
              display={(v) => facets.labels?.[v] || v}
              testid="facet-material"
            />
            <FacetRow
              label={t('common.colors')}
              items={facets.colors}
              active={filters.color}
              onPick={(v) => setFilter('color', v)}
              display={(v) => facets.labels?.[v] || v}
              testid="facet-color"
            />
          </div>
        </div>
      )}

      <div className="wrap pb-24">
        {products.isPending ? (
          <div
            data-testid="category-grid"
            className="grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-3 lg:grid-cols-4"
            aria-busy="true"
          >
            {Array.from({ length: 8 }, (_, i) => (
              <TileSkeleton key={i} />
            ))}
          </div>
        ) : products.isError ? (
          <p className="prosa">{t('catalog.loadError')}</p>
        ) : items.length === 0 ? (
          <p className="py-16 text-center font-display text-2xl text-inchiostro/50">
            {t('catalog.empty')}
          </p>
        ) : (
          <div
            data-testid="category-grid"
            className={`grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-3 lg:grid-cols-4 ${
              products.isFetching ? 'opacity-60' : ''
            }`}
          >
            {withInspiration(items, INSPIRATION[cat.data?.macro?.slug], cat.data?.label || '')}
          </div>
        )}

        {pages > 1 && (
          <div className="mt-16 flex items-center justify-center gap-3">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => goToPage(page - 1)}
              data-testid="page-prev"
              className="btn btn-line disabled:opacity-30"
            >
              ←
            </button>
            <span className="text-[14px] text-inchiostro/70">
              {page} / {pages}
            </span>
            <button
              type="button"
              disabled={page >= pages}
              onClick={() => goToPage(page + 1)}
              data-testid="page-next"
              className="btn btn-line disabled:opacity-30"
            >
              →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
